/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/**
 * WatchlistSummary — the Watchlist "Summary" tab: a horizon-scan-style rollup across all watchlist
 * risks. Top = aggregate stats; bottom = per-risk cards with each risk's top Horizon Scan themes.
 * Reads GET /emergingRisk/watchlist-summary.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { protectedResources } from "authConfig";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const API = protectedResources.apiTodoList.endpoint;
const C = {
  ink: "#152230",
  muted: "#5B6B78",
  line: "#E3E8EC",
  accent: "#52009b",
};

const trendStyle = {
  worsening: { label: "⚠ Worsening", color: "#b71c1c", bg: "#ffebee" },
  improving: { label: "✓ Improving", color: "#1b5e20", bg: "#e8f5e9" },
  stable: { label: "~ Stable", color: "#37474f", bg: "#eceff1" },
};

function Stat({ label, value, color }) {
  return (
    <Box
      sx={{
        flex: "1 1 130px",
        minWidth: 120,
        border: `1px solid ${C.line}`,
        borderRadius: "12px",
        p: 1.5,
        background: "#fff",
      }}
    >
      <MDTypography variant="h4" sx={{ color: color || C.ink, lineHeight: 1 }}>
        {value}
      </MDTypography>
      <MDTypography
        variant="caption"
        sx={{
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: C.muted,
          fontWeight: 700,
          marginTop: "6px",
        }}
      >
        {label}
      </MDTypography>
    </Box>
  );
}

const THEME_CHIP = {
  opportunity: { c: "#2e7d32", b: "#e8f5e9" },
  both: { c: "#8a6d00", b: "#fdf3d0" },
  threat: { c: "#c62828", b: "#fdecea" },
};
function ThemeChip({ t }) {
  const s = THEME_CHIP[t.threatOrOpportunity] || THEME_CHIP.threat;
  return (
    <span
      style={{
        fontSize: "0.75rem",
        fontWeight: 600,
        color: s.c,
        background: s.b,
        borderRadius: 6,
        padding: "2px 8px",
      }}
    >
      {t.theme}
    </span>
  );
}

function WatchlistSummary({ execute, onOpenRisk }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await executeRef.current("GET", `${API}/emergingRisk/watchlist-summary`);
      if (res?.success) setData(res.data || null);
    } catch (e) {
      /* handled by hook */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: "center", color: C.muted }}>
        <CircularProgress size={22} sx={{ color: C.accent }} />
      </Box>
    );
  }

  const totals = data?.totals || {};
  const risks = data?.risks || [];

  return (
    <MDBox sx={{ p: 2 }}>
      {/* Top — aggregate summary */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
        <Stat label="Watchlist risks" value={totals.total || 0} />
        <Stat label="Active" value={totals.active || 0} color="#2e7d32" />
        <Stat label="Draft" value={totals.draft || 0} />
        <Stat label="Worsening" value={totals.worsening || 0} color="#b71c1c" />
        <Stat label="Improving" value={totals.improving || 0} color="#1b5e20" />
        <Stat label="Threat themes" value={totals.threatThemes || 0} color="#c62828" />
        <Stat label="Opportunity themes" value={totals.opportunityThemes || 0} color="#2e7d32" />
      </Box>

      {/* Bottom — per-risk themes detail */}
      <MDTypography variant="h6" sx={{ mb: 1.5, color: C.ink }}>
        Risks &amp; emerging themes
      </MDTypography>
      {risks.length === 0 ? (
        <MDTypography variant="body2" sx={{ color: C.muted }}>
          No watchlist risks yet.
        </MDTypography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {risks.map((r) => {
            const tr = trendStyle[r.trendDirection] || trendStyle.stable;
            return (
              <Box
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={() => onOpenRisk && onOpenRisk(r.id)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && onOpenRisk) onOpenRisk(r.id);
                }}
                sx={{
                  border: `1px solid ${C.line}`,
                  borderLeft: `5px solid ${r.status === "active" ? "#2e7d32" : "#b0bec5"}`,
                  borderRadius: "12px",
                  p: 1.75,
                  background: "#fff",
                  cursor: "pointer",
                  "&:hover": { boxShadow: "0 1px 6px rgba(0,0,0,0.08)" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <MDTypography variant="h6" sx={{ fontWeight: 700, color: C.ink }}>
                    {r.title || "(untitled risk)"}
                  </MDTypography>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <span
                      style={{
                        fontSize: "0.6875rem",
                        fontWeight: 700,
                        color: tr.color,
                        background: tr.bg,
                        borderRadius: 6,
                        padding: "1px 7px",
                      }}
                    >
                      {tr.label}
                    </span>
                    <MDTypography
                      component="span"
                      variant="caption"
                      sx={{
                        display: "inline",
                        fontWeight: 700,
                        textTransform: "capitalize",
                        color: r.status === "active" ? "#2e7d32" : C.muted,
                      }}
                    >
                      {r.status}
                    </MDTypography>
                  </div>
                </Box>
                <MDTypography variant="caption" sx={{ color: C.muted, marginTop: "2px" }}>
                  {[r.category, r.subCategory].filter(Boolean).join(" › ")}
                  {r.likelihood ? ` · ${r.likelihood}` : ""}
                </MDTypography>
                {r.topThemes && r.topThemes.length > 0 ? (
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                    {r.topThemes.map((t, i) => (
                      <ThemeChip key={i} t={t} />
                    ))}
                    {r.themeCount > r.topThemes.length && (
                      <MDTypography
                        component="span"
                        variant="caption"
                        sx={{ display: "inline", color: C.muted, alignSelf: "center" }}
                      >
                        +{r.themeCount - r.topThemes.length} more
                      </MDTypography>
                    )}
                  </Box>
                ) : (
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: C.muted,
                      marginTop: "6px",
                      fontStyle: "italic",
                    }}
                  >
                    No horizon-scan themes yet.
                  </MDTypography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </MDBox>
  );
}

export default WatchlistSummary;
